import {
  breakpointSizes,
  defaultTheme,
  getBreakpointDown,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { spacing } from 'ext-ui/style/spacing'
import ThumbsDownIcon from 'ext-ui/svg/icon/ThumbsDownIcon'
import ThumbsUpIcon from 'ext-ui/svg/icon/ThumbsUpIcon'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectExpenseReportIdFromRoute,
  selectExpenseReportLoadedFromState,
  selectExpenseReportModelFromState,
  selectMyWalletReimbursementIdFromRoute,
  selectResetBulkSelectFromState,
  selectSubmittingExpenseReportFromState,
} from 'src/app/ExpenseReport/redux/expenseReport.selectors'
import SvgRejectTrash from 'src/app/ExpenseReport/SvgRejectTrash'
import { ReceiptAutoMatchPushTaskStatus } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.reducers'
import { selectReceiptsAutoMatchPushTaskListFromState } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import Sparkles from 'src/assets/svgs/Sparkles.svg'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import DeactivatedUserIndicator from 'src/components/DeactivatedUserIndicator/DeactivatedUserIndicator'
import ExpensesDataGrid from 'src/components/ExpensesDataGrid/ExpensesDataGrid'
import FloatingToolbar from 'src/components/FloatingToolbar/FloatingToolbar'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import SvgClose from 'src/components/Svgs/SvgClose'
import SvgLocked from 'src/components/Svgs/SvgLocked'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import TestId from 'src/components/TestId/TestId'
import TextWithUpdateInputField from 'src/components/TextWithUpdateInputField/TextWithUpdateInputField'
import { ExtendCurrency } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import Crncy from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import {
  ExpenseReportStatus,
  ExpenseType,
} from 'src/models/expenseReport.model'
import { addExpenseUrl } from 'src/navigation/urls'
import {
  createSetIsAttachReceiptsExpenseReportPopupOpen,
  createSetIsDeleteExpensePopupOpen,
  createSetIsMarkExpenseReportsPaidPopupOpen,
  createSetIsSubmitExpenseReportPopupOpen,
} from 'src/redux/actions/app.actions'
import {
  CLEAR_EXPENSE_REPORT,
  getExpenseReport,
  patchUpdateExpenseReport,
  postApproveExpenses,
  postRejectExpenses,
  RESET_BULK_SELECT,
} from 'src/redux/actions/data/expenses.actions'
import { selectIsCreateDeleteExpensePopupOpenFromState } from 'src/redux/selectors/app.selectors'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectOutOfPocketExpensesVisible,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { toDollars } from 'src/utils/currency.utils'
import styled, { css } from 'styled-components'

import style from './ExpenseReport.style'

const StyledSpanLabel = styled.span`
  text-align: center;
  border-radius: 5px;
  padding: 4px;
  display: inline-block;
  vertical-align: top;
  margin-top: 6px;
`

const ExpenseReportStatusLabel = (
  status: ExpenseReportStatus,
  userIsApprover: boolean,
): React.ReactElement => {
  switch (status) {
    case ExpenseReportStatus.Draft: {
      return (
        <StyledSpanLabel
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.black,
              styleConstants.fontFamilies.medium,
            )}
            background-color: ${styleConstants.colors.gray_medium};
          `}
        >
          <Trns source={'Not Submitted'} />
        </StyledSpanLabel>
      )
    }
    case ExpenseReportStatus.Pending: {
      return (
        <StyledSpanLabel
          css={css`
            ${getFontCss(12, '#856700', styleConstants.fontFamilies.medium)}
            background-color: rgba(252, 241, 202, 1);
            ${userIsApprover &&
            css`
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
              background-color: ${styleConstants.colors.gray_medium};
            `}
          `}
        >
          {userIsApprover ? (
            <Trns source={'Pending Approval'} />
          ) : (
            <Trns source={'Submitted'} />
          )}
        </StyledSpanLabel>
      )
    }
    case ExpenseReportStatus.Paid: {
      return (
        <StyledSpanLabel
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.green_darkest,
              styleConstants.fontFamilies.medium,
            )}
            background-color: ${styleConstants.colors.green_light};
          `}
        >
          <Trns source={'Paid'} />
        </StyledSpanLabel>
      )
    }
    case ExpenseReportStatus.Rejected: {
      return (
        <StyledSpanLabel
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.red_dark,
              styleConstants.fontFamilies.medium,
            )}
            background-color: #FAD3D6;
          `}
        >
          <Trns source={'Rejected'} />
        </StyledSpanLabel>
      )
    }
    case ExpenseReportStatus.Reviewed: {
      return (
        <StyledSpanLabel
          css={css`
            ${getFontCss(12, '#856700', styleConstants.fontFamilies.medium)}
            background-color: rgba(252, 241, 202, 1);
          `}
        >
          <Trns source={'Approved'} />
        </StyledSpanLabel>
      )
    }
    default: {
      return <></>
    }
  }
}

export enum ExpenseReportContext {
  myWallet = 'myWallet',
  managerReview = 'managerReview',
  accounting = 'accounting',
}

interface ExpenseReportProps {
  context: ExpenseReportContext
}

const ExpenseReport: React.FC<ExpenseReportProps> = (props) => {
  const expenseReportId =
    props.context === ExpenseReportContext.myWallet
      ? useSelector(selectMyWalletReimbursementIdFromRoute)
      : useSelector(selectExpenseReportIdFromRoute)

  const expenseReport = useSelector(selectExpenseReportModelFromState)
  const loaded = useSelector(selectExpenseReportLoadedFromState)
  const user = useSelector(selectUserListModelFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const submittingExpenseReport = useSelector(
    selectSubmittingExpenseReportFromState,
  )
  const outOfPocketExpensesEnabled = useSelector(
    selectOutOfPocketExpensesVisible,
  )
  const isDeleteExpensePopupOpen = useSelector(
    selectIsCreateDeleteExpensePopupOpenFromState,
  )
  const resetBulkSelect = useSelector(selectResetBulkSelectFromState)

  const receiptsAutoMatchPushTaskList = useSelector(
    selectReceiptsAutoMatchPushTaskListFromState,
  )
  const completedReceiptsAutoMatchTasks = receiptsAutoMatchPushTaskList.filter(
    (task) => task.status !== ReceiptAutoMatchPushTaskStatus.PENDING,
  )

  const [showAutoCreateMessage, setShowAutoCreateMessage] =
    useState<boolean>(false)
  const [bulkSelected, setBulkSelected] = useState<boolean>(false)
  const [selectedExpenses, setSelectedExpenses] = useState<string[]>([])
  const [titleWidth, setTitleWidth] = useState<number | undefined>()

  const dispatch = useBackgroundDispatch()

  const userIsApprover = user?.id === organization?.expenseApprover?.id
  const userIsSubmitter = user?.id === expenseReport?.employeeId
  const isExpenseReportStatusDraft =
    expenseReport !== undefined &&
    expenseReport.status === ExpenseReportStatus.Draft
  const isReportMissingReceipts = expenseReport?.expenses
    ?.filter((expense) => expense.type !== ExpenseType.Mileage)
    .some((expense) => expense.receiptAttachments?.length === 0)

  const { analytics } = useAnalyticsContext()

  useEffect(() => {
    if (
      completedReceiptsAutoMatchTasks.length ===
        receiptsAutoMatchPushTaskList.length &&
      completedReceiptsAutoMatchTasks.length > 0
    ) {
      const successTasks = receiptsAutoMatchPushTaskList.filter(
        (task) => task.status === ReceiptAutoMatchPushTaskStatus.SUCCESS,
      )
      if (successTasks.length > 0) {
        dispatch(getExpenseReport(expenseReportId))
        setShowAutoCreateMessage(true)
      }
    }
  }, [
    completedReceiptsAutoMatchTasks.length,
    receiptsAutoMatchPushTaskList.length,
  ])

  useEffect(() => {
    dispatch(getExpenseReport(expenseReportId))

    return (): void => {
      dispatch({ type: CLEAR_EXPENSE_REPORT })
    }
  }, [expenseReportId])

  useEffect(() => {
    if (!isDeleteExpensePopupOpen || resetBulkSelect) {
      setBulkSelected(false)
      setSelectedExpenses([])
      if (resetBulkSelect) {
        dispatch({ type: RESET_BULK_SELECT })
      }
    }
  }, [isDeleteExpensePopupOpen, resetBulkSelect])

  const showCheckboxes = (): boolean => {
    if (expenseReport?.status === ExpenseReportStatus.Draft) {
      return true
    } else if (
      expenseReport?.status === ExpenseReportStatus.Pending &&
      userIsApprover
    ) {
      return true
    }
    return false
  }

  const handleExpenseCheckboxClick = (option: string): void => {
    if (!selectedExpenses.includes(option)) {
      if (
        expenseReport?.expenses &&
        selectedExpenses.length + 1 === expenseReport?.expenses.length
      ) {
        setBulkSelected(true)
      }
      setSelectedExpenses((prev) => [...prev, option])
    } else {
      setSelectedExpenses((prev) => prev.filter((item) => item !== option))
      if (bulkSelected) {
        setBulkSelected(false)
      }
    }
  }

  const handleBulkExpenseCheckboxClick = (): void => {
    if (expenseReport?.expenses !== undefined) {
      bulkSelected
        ? setSelectedExpenses([])
        : setSelectedExpenses(
            expenseReport.expenses.map((expense) => expense.id) || [],
          )
    }
    setBulkSelected(!bulkSelected)
  }

  const handleCloseToolbar = (): void => {
    setBulkSelected(false)
    setSelectedExpenses([])
  }

  const handleReportSubmit = (): void => {
    if (isReportMissingReceipts) {
      dispatch(createSetIsAttachReceiptsExpenseReportPopupOpen(true))
    } else {
      dispatch(
        createSetIsSubmitExpenseReportPopupOpen(
          true,
          expenseReport?.expenses.length,
        ),
      )
    }
  }

  const handleApproveSubmit = async (): Promise<void> => {
    await dispatch(postApproveExpenses(selectedExpenses, analytics))
    handleCloseToolbar()
  }

  const handleRejectSubmit = async (): Promise<void> => {
    await dispatch(postRejectExpenses(selectedExpenses, analytics))
    handleCloseToolbar()
  }

  const handleDeleteExpensesSubmit = (): void => {
    dispatch(createSetIsDeleteExpensePopupOpen(true, selectedExpenses))
  }

  const handleReportPaidSubmit = (): void => {
    dispatch(
      createSetIsMarkExpenseReportsPaidPopupOpen(
        true,
        [expenseReportId],
        expenseReport?.approvedAmountCents,
      ),
    )
  }

  const handleSaveExpenseReportName = (value: string): void => {
    dispatch(patchUpdateExpenseReport(value, expenseReportId))
  }

  const showMarkAsPaidButton =
    (expenseReport?.status === ExpenseReportStatus.Reviewed ||
      expenseReport?.status === ExpenseReportStatus.Pending) &&
    userIsApprover

  const showLockedCantEdit =
    (expenseReport?.status === ExpenseReportStatus.Paid ||
      expenseReport?.status === ExpenseReportStatus.Rejected) &&
    (userIsApprover || userIsSubmitter)

  const showLockedCantAdd =
    (expenseReport?.status === ExpenseReportStatus.Pending ||
      expenseReport?.status === ExpenseReportStatus.Reviewed) &&
    userIsSubmitter

  return outOfPocketExpensesEnabled && expenseReport !== undefined ? (
    <BackgroundPage>
      <BackgroundPageContent className="ExpenseReport" css={style}>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            ${getBreakpointDown(breakpointSizes.MD)(css`
              padding: 0px;
            `)}
            gap: 24px;
          `}
        >
          {(showMarkAsPaidButton ||
            showLockedCantEdit ||
            showLockedCantAdd) && (
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  flex-direction: column;
                  row-gap: 10px;
                `)}
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-end;
                  margin-left: auto;
                  gap: ${spacing(1)};
                `}
              >
                {showMarkAsPaidButton && (
                  <Button
                    type={ButtonTypes.button}
                    design={ButtonDesigns.designCta}
                    text={<Trns source={'Mark Report as Paid'} />}
                    onClick={handleReportPaidSubmit}
                    disabled={
                      expenseReport.status === ExpenseReportStatus.Pending
                    }
                  />
                )}
                {showLockedCantEdit && (
                  <span
                    css={css`
                      display: flex;
                      align-items: center;
                      ${getFontCss(
                        14,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  >
                    <SvgLocked
                      fill={styleConstants.colors.gray_darkest}
                      css={css`
                        transform: scale(calc(16 / 24));
                        margin-bottom: 2px;
                      `}
                    />
                    <Trns source={'Expenses can no longer be edited'} />
                  </span>
                )}
                {showLockedCantAdd && (
                  <span
                    css={css`
                      display: flex;
                      align-items: center;
                      ${getFontCss(
                        14,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  >
                    <SvgLocked
                      fill={styleConstants.colors.gray_darkest}
                      css={css`
                        transform: scale(calc(16 / 24));
                        margin-bottom: 2px;
                      `}
                    />
                    <Trns source={'Expenses can no longer be added'} />
                  </span>
                )}
              </div>
            </div>
          )}
          <div
            css={css`
              display: flex;
              column-gap: 20px;
              ${getBreakpointDown(breakpointSizes.SM)(css`
                flex-direction: column;
                row-gap: 20px;
              `)}
            `}
          >
            <div
              css={css`
                display: flex;
                row-gap: 6px;
                flex-direction: column;
                flex-grow: 1;
              `}
            >
              <div
                ref={(el): void => {
                  if (el !== null) {
                    setTitleWidth(el?.getBoundingClientRect().width)
                  }
                }}
                css={css`
                  display: flex;
                  align-items: center;
                  column-gap: 16px;
                  padding-bottom: 20px;
                  ${getFontCss(
                    32,
                    styleConstants.colors.black,
                    styleConstants.fontFamilies.medium,
                  )}
                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                    ${getFontCss(
                      24,
                      styleConstants.colors.black,
                      styleConstants.fontFamilies.medium,
                    )}
                  `)}
                `}
              >
                <TextWithUpdateInputField
                  value={expenseReport.reportName}
                  onSave={handleSaveExpenseReportName}
                  isEditable={
                    expenseReport.status === ExpenseReportStatus.Draft
                  }
                  characterLimit={40}
                  maxWidth={titleWidth && titleWidth - 170}
                  tooltipText={<Trns source={'Edit Report Name'} />}
                />
                {props.context !== ExpenseReportContext.managerReview &&
                  ExpenseReportStatusLabel(
                    expenseReport.status,
                    userIsApprover,
                  )}
                <div
                  css={css`
                    flex-grow: 1;
                  `}
                />
              </div>
              {expenseReport.status !== ExpenseReportStatus.Draft &&
                !userIsSubmitter && (
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                      display: flex;
                      flex-direction: row;
                    `}
                  >
                    <Trns
                      source={'Report by ${name}'}
                      expressions={{
                        name: expenseReport.employee,
                      }}
                    />
                    {expenseReport?.deactivatedUser && (
                      <DeactivatedUserIndicator />
                    )}
                  </span>
                )}
              {expenseReport.status !== ExpenseReportStatus.Draft &&
                userIsSubmitter && (
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )}
                    `}
                  >
                    <Trns
                      source={'Submitted on ${date}'}
                      expressions={{
                        date: moment(expenseReport.submittedAtDate).format('l'),
                      }}
                    />
                  </span>
                )}
            </div>
            {isExpenseReportStatusDraft && (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  column-gap: 40px;
                `}
              >
                <HoverableLink
                  ariaLabelSource={'Add Expense'}
                  to={addExpenseUrl(expenseReportId)}
                >
                  <Trns source={'+ Add Expense'} />
                </HoverableLink>
                <TestId testId={'submitReport'}>
                  <Button
                    type={ButtonTypes.button}
                    design={ButtonDesigns.designCta}
                    text={<Trns source={'Submit Report'} />}
                    onClick={handleReportSubmit}
                    loading={submittingExpenseReport}
                  />
                </TestId>
              </div>
            )}
            {expenseReport.status !== ExpenseReportStatus.Draft && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-end;
                  justify-content: flex-end;
                `}
              >
                <span
                  css={css`
                    ${getFontCss(
                      20,
                      styleConstants.colors.black,
                      styleConstants.fontFamilies.medium,
                    )}
                  `}
                >
                  <Crncy
                    currency={
                      expenseReport.approvedAmountCurrency !== undefined
                        ? expenseReport.approvedAmountCurrency
                        : ExtendCurrency.Usd
                    }
                    amount={
                      expenseReport.approvedAmountCents !== undefined
                        ? toDollars(expenseReport.approvedAmountCents)
                        : 0
                    }
                  />
                  {' / '}
                  <Crncy
                    currency={
                      expenseReport.totalAmountSubmittedCurrency !== undefined
                        ? expenseReport.totalAmountSubmittedCurrency
                        : ExtendCurrency.Usd
                    }
                    amount={
                      expenseReport.totalAmountSubmittedCents !== undefined
                        ? toDollars(expenseReport.totalAmountSubmittedCents)
                        : 0
                    }
                  />
                </span>
                <span
                  css={css`
                    ${getFontCss(
                      12,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )}
                  `}
                >
                  <Trns source={'Amount to be reimbursed'} />
                </span>
              </div>
            )}
          </div>
          {expenseReport.status === ExpenseReportStatus.Draft &&
            isReportMissingReceipts && (
              <span
                css={css`
                  ${getFontCss(
                    14,
                    styleConstants.colors.red,
                    styleConstants.fontFamilies.regular,
                  )}
                  margin-left: 14px;
                `}
              >
                <Trns
                  source={
                    'There are expenses missing receipts. Please attach your receipts to submit your report.'
                  }
                />
              </span>
            )}
          {showAutoCreateMessage && (
            <div
              css={css`
                border-radius: 10px;
                border: 1px solid ${styleConstants.colors.purple_dark};
                background-color: rgb(79, 63, 221, 0.25);
                padding: 10px;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 10px;
              `}
            >
              <img
                src={Sparkles}
                alt={'sparkles'}
                css={css`
                  filter: brightness(0) saturate(100%) invert(16%) sepia(97%)
                    saturate(4068%) hue-rotate(251deg) brightness(94%)
                    contrast(92%);
                  width: 24px;
                  height: 24px;
                  margin-left: 16px;
                `}
              />
              <span
                css={css`
                  ${getFontCss(
                    14,
                    styleConstants.colors.purple,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                <Trns
                  source={
                    'This report contains expenses created from your uploaded receipts. Review and make any changes before submitting your report.'
                  }
                />
              </span>
              <SvgClose
                onClick={(): void => {
                  setShowAutoCreateMessage(false)
                }}
                css={css`
                  filter: brightness(0) saturate(100%) invert(16%) sepia(97%)
                    saturate(4068%) hue-rotate(251deg) brightness(94%)
                    contrast(92%);
                  margin-left: auto;
                  cursor: pointer;
                `}
              />
            </div>
          )}
          {expenseReport.expenses.length === 0 ? (
            <Box>
              <TableEmptyState
                design={
                  isExpenseReportStatusDraft
                    ? TableEmptyStateDesigns.SubmitterReportDetail
                    : user?.fullNameOrEmail === expenseReport.employee
                      ? TableEmptyStateDesigns.UserIsViewingTheirReport
                      : TableEmptyStateDesigns.ApproverReportDetail
                }
                expenseReportId={
                  isExpenseReportStatusDraft ? expenseReportId : undefined
                }
                wide={isExpenseReportStatusDraft}
                isLink={isExpenseReportStatusDraft}
              />
            </Box>
          ) : (
            <ExpensesDataGrid
              expenses={expenseReport.expenses}
              expenseReportStatus={expenseReport.status}
              bulkSelected={bulkSelected}
              selectedExpenses={selectedExpenses}
              handleExpenseCheckboxClick={handleExpenseCheckboxClick}
              handleBulkExpenseCheckboxClick={handleBulkExpenseCheckboxClick}
              showCheckbox={showCheckboxes()}
              isDraftStatus={isExpenseReportStatusDraft}
              context={props.context}
            />
          )}
          {props.context !== ExpenseReportContext.managerReview && (
            <FloatingToolbar
              visible={selectedExpenses.length > 0}
              noPadding
              noColumnGap
              css={css`
                display: flex;
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                align-items: center;
                ${getBreakpointUp(breakpointSizes.MD)(css`
                  height: 60px;
                `)}
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    16,
                    styleConstants.colors.black,
                    styleConstants.fontFamilies.medium,
                  )}
                  padding: 20px 40px;
                `}
              >
                {selectedExpenses.length > 1 ? (
                  <Trns
                    source={'${expenses} expenses selected'}
                    expressions={{
                      expenses: selectedExpenses.length.toString(),
                    }}
                  />
                ) : (
                  <Trns
                    source={'1 expense selected'}
                    expressions={{
                      expenses: selectedExpenses.length.toString(),
                    }}
                  />
                )}
              </span>
              <>
                {isExpenseReportStatusDraft ? (
                  <TestId
                    testId={'deleteButton'}
                    css={css`
                      height: 100%;
                      align-items: center;
                      border-left: 1px solid
                        ${styleConstants.colors.gray_medium};
                      border-right: 1px solid
                        ${styleConstants.colors.gray_medium};
                    `}
                  >
                    <HoverableLink
                      css={css`
                        height: 100%;
                        padding: 8px 8px;
                      `}
                      onClick={handleDeleteExpensesSubmit}
                      ariaLabel="Delete"
                    >
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 4px;
                          padding: 10px;
                          border-radius: 4px;
                          ${getFontCss(
                            16,
                            styleConstants.colors.red,
                            styleConstants.fontFamilies.regular,
                          )};
                          &:hover {
                            background-color: ${styleConstants.colors
                              .blue_opacity};
                          }
                        `}
                      >
                        <SvgRejectTrash fill={styleConstants.colors.red} />
                        <Trns
                          css={css`
                            margin-left: 8px;
                          `}
                          source={'Delete'}
                        />
                      </div>
                    </HoverableLink>
                  </TestId>
                ) : (
                  <>
                    <TestId
                      testId={'approveButton'}
                      css={css`
                        height: 100%;
                        align-items: center;
                        border-left: 1px solid
                          ${styleConstants.colors.gray_medium};
                      `}
                    >
                      <HoverableLink
                        css={css`
                          height: 100%;
                          padding: 8px 8px;
                        `}
                        onClick={handleApproveSubmit}
                        ariaLabel="Approve"
                      >
                        <div
                          css={css`
                            display: flex;
                            align-items: center;
                            gap: 4px;
                            padding: 10px;
                            border-radius: 4px;
                            ${getFontCss(
                              16,
                              themeConstants.colors.cta,
                              styleConstants.fontFamilies.regular,
                            )};
                            &:hover {
                              background-color: ${styleConstants.colors
                                .blue_opacity};
                            }
                          `}
                        >
                          <ThumbsUpIcon stroke={defaultTheme.colors.cta} />
                          <Trns
                            css={css`
                              margin-left: 8px;
                            `}
                            source={'Approve'}
                          />
                        </div>
                      </HoverableLink>
                    </TestId>
                    <TestId
                      testId={'rejectButton'}
                      css={css`
                        height: 100%;
                        align-items: center;
                        border-left: 1px solid
                          ${styleConstants.colors.gray_medium};
                        border-right: 1px solid
                          ${styleConstants.colors.gray_medium};
                      `}
                    >
                      <HoverableLink
                        css={css`
                          height: 100%;
                          padding: 8px 8px;
                        `}
                        onClick={handleRejectSubmit}
                        ariaLabel="Reject"
                      >
                        <div
                          css={css`
                            display: flex;
                            align-items: center;
                            gap: 4px;
                            padding: 10px;
                            border-radius: 4px;
                            ${getFontCss(
                              16,
                              styleConstants.colors.red,
                              styleConstants.fontFamilies.regular,
                            )};
                            &:hover {
                              background-color: ${styleConstants.colors
                                .blue_opacity};
                            }
                          `}
                        >
                          <ThumbsDownIcon stroke={styleConstants.colors.red} />
                          <Trns
                            css={css`
                              margin-left: 8px;
                            `}
                            source={'Reject'}
                          />
                        </div>
                      </HoverableLink>
                    </TestId>
                  </>
                )}
                <HoverableLink
                  ariaLabelSource={'Close'}
                  onClick={handleCloseToolbar}
                >
                  <div
                    css={css`
                      display: flex;
                      position: relative;
                      width: 36px;
                      height: 36px;
                      align-items: center;
                      justify-content: center;
                      margin: 0px 8px;
                      svg {
                        transition: stroke 0.2s ease;
                      }
                      svg path {
                        stroke: ${themeConstants.colors.font_primary};
                        transition: stroke-width 0.2s ease;
                      }
                      &:hover {
                        svg path {
                          stroke: ${themeConstants.colors.cta};
                        }
                        &::before {
                          content: '';
                          position: absolute;
                          top: 0;
                          left: 0;
                          width: 100%;
                          height: 100%;
                          background-color: rgba(0, 0, 0, 0.05);
                          border-radius: 8px;
                          transition: background-color 0.2s ease;
                        }
                      }
                    `}
                  >
                    <SvgClose />
                  </div>
                </HoverableLink>
              </>
            </FloatingToolbar>
          )}
        </div>
      </BackgroundPageContent>
    </BackgroundPage>
  ) : loaded ? (
    <NotFoundBackgroundPage />
  ) : (
    <LoadingBackgroundPage />
  )
}

export default ExpenseReport
